import {
  effectiveWebIdentifiers,
  ensureUrlScheme,
  normalizedHost,
  siteRuleFor,
  SHIPPED_SITE_RULES,
} from '@heybesafe/shared';
import type { SiteRulesBundle, VaultItem } from '@heybesafe/shared';
import { registrableDomain } from '../utils/faviconUrl';

/**
 * Where the Security Checkup "Fix" action sends the user to actually change a
 * password: the site's dedicated change-password page when the site-rules bundle
 * knows one (`SiteRule.changePasswordUrl`), otherwise the **exact URL the user
 * saved** (their launch URL — the same thing the row's "Launch Site" opens).
 * Returns `null` when the login carries no usable web address (no resolvable
 * host) — the caller then falls back to the in-app editor so the button still
 * does something for a URL-less login.
 *
 * The fallback is the saved URL in full, NOT a synthesized `https://<host>/`.
 * Stripping a saved deep link to its host root 404s on sites whose login lives on
 * a subdomain that serves no page at `/` — e.g. a saved
 * `https://connect.secure.wellsfargo.com/auth/login/present?origin=irt` became
 * `https://connect.secure.wellsfargo.com/`, which 404s. The saved URL is one the
 * user actually uses, so it resolves; from their login page they reach the
 * password settings. (This is why web diverges from the extension's
 * `START_PASSWORD_CHANGE`, which falls back to the *current page's* host root —
 * there the host is the live page you're on, not a saved deep link.)
 *
 * The rule lookup still uses the host and its registrable domain. Defaults to the
 * shipped offline snapshot (`SHIPPED_SITE_RULES`), which bakes in a
 * change-password-URL-only `sites` map (Apple's `change-password-URLs.json`, via
 * `scripts/site-rules/emit-snapshot.mjs`) — so a known site resolves to its
 * dedicated change-password page with no network, correct as of the last client
 * build. The full, fresher `sites` lives in the published signed bundle
 * (`build-bundle.mjs`), fetched at runtime by the extension; the web client has
 * no such store yet, so pass that bundle in here if it ever gains one. The
 * `bundle` parameter also exists for tests.
 */
export function changePasswordTarget(
  item: VaultItem,
  bundle: SiteRulesBundle = SHIPPED_SITE_RULES,
): string | null {
  const primary = ensureUrlScheme(effectiveWebIdentifiers(item)[0] ?? item.url ?? '');
  const host = primary ? normalizedHost(primary) : '';
  // A real host has a dot (`example.com`); a bare unparseable token does not,
  // so we don't treat a malformed identifier as a site.
  if (!host || !host.includes('.')) return null;
  const domain = registrableDomain(host);
  const rule = siteRuleFor(bundle, host) ?? siteRuleFor(bundle, domain);
  // Known change-password page → it; otherwise the full saved URL (never the
  // bare host root, which 404s for a deep-link subdomain).
  return rule?.changePasswordUrl ?? primary;
}
